import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Button,
  message,
  Space,
  TreeSelect
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import '@wangeditor/editor/dist/css/style.css';
import ImageUpload from '../../components/ImageUpload';

const { Option } = Select;

const HelpManualForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');
  const [content, setContent] = useState('<p><br></p>');
  const [editor, setEditor] = useState(null);
  const [treeData, setTreeData] = useState([]);
  const [manualType, setManualType] = useState(1);

  useEffect(() => {
    fetchTreeData();
    if (id) {
      fetchManualDetail();
    }
  }, [id]);

  // 获取树形结构数据
  const fetchTreeData = async () => {
    try {
      const response = await api.get('/admin/help-manuals/tree');
      if (response.code === 200) {
        const tree = buildTreeSelectData(response.data, id);
        setTreeData([
          { title: '顶级目录', value: 0, children: tree }
        ]);
      }
    } catch (error) {
      console.error('获取树形数据失败:', error);
    }
  };

  // 构建TreeSelect数据
  const buildTreeSelectData = (data, currentId) => {
    return data.map(item => ({
      title: item.title,
      value: item.id,
      disabled: item.id === parseInt(currentId) || item.type === 1, // 禁用自己和文档类型
      children: item.children ? buildTreeSelectData(item.children, currentId) : undefined
    }));
  };

  const fetchManualDetail = async () => {
    try {
      const response = await api.get(`/admin/help-manuals/${id}`);
      if (response.code === 200) {
        const manual = response.data;
        
        form.setFieldsValue({
          parentId: manual.parentId,
          title: manual.title,
          type: manual.type,
          cover: manual.cover,
          content: manual.content,
          sortOrder: manual.sortOrder,
          status: manual.status === 1,
        });
        
        setManualType(manual.type);
        setCoverUrl(manual.cover || '');
        setContent(manual.content || '<p><br></p>');
      } else {
        message.error(response.message || '获取帮助手册详情失败');
      }
    } catch (error) {
      message.error('获取帮助手册详情失败');
    }
  };

  // 处理富文本内容变化
  const handleContentChange = (editor) => {
    const html = editor.getHtml();
    setContent(html);
    form.setFieldsValue({ content: html });
  };

  // 组件销毁时销毁编辑器
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  // 处理封面图片变化
  const handleCoverChange = (value) => {
    setCoverUrl(value);
    form.setFieldsValue({ cover: value });
  };

  // 处理类型变化
  const handleTypeChange = (value) => {
    setManualType(value);
  };

  // 编辑器配置
  const editorConfig = {
    placeholder: '请输入帮助手册内容...',
    MENU_CONF: {
      uploadImage: {
        async customUpload(file, insertFn) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('type', 'general');
          
          try {
            const response = await api.post('/common/upload', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            
            if (response.code === 200 && response.data && response.data.fileUrl) {
              insertFn(response.data.fileUrl, file.name, response.data.fileUrl);
            } else {
              message.error(response.message || '图片上传失败');
            }
          } catch (error) {
            message.error('图片上传失败: ' + (error.message || '未知错误'));
          }
        }
      }
    }
  };

  // 工具栏配置
  const toolbarConfig = {
    toolbarKeys: [
      'headerSelect',
      'bold',
      'italic',
      'underline',
      'through',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'fontSize',
      'color',
      'bgColor',
      '|',
      'insertLink',
      'uploadImage',
      '|',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      '|',
      'undo',
      'redo',
    ]
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const params = {
        ...values,
        status: values.status ? 1 : 0,
        cover: coverUrl || '',
        parentId: values.parentId || 0,
      };
      
      let response;
      if (id) {
        response = await api.put(`/admin/help-manuals/${id}`, params);
      } else {
        response = await api.post('/admin/help-manuals', params);
      }
      
      if (response.code === 200) {
        message.success(id ? '更新成功' : '创建成功');
        navigate('/help-manuals');
      } else {
        message.error(response.message || (id ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      message.error(error.message || (id ? '更新失败' : '创建失败'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card 
      title={id ? '编辑帮助手册' : '新建帮助手册'}
      extra={
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => navigate('/help-manuals')}
        >
          返回列表
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          parentId: 0,
          type: 1,
          sortOrder: 0,
          status: true,
          content: '<p><br></p>',
        }}
      >
        <Form.Item
          label="父级目录"
          name="parentId"
        >
          <TreeSelect
            treeData={treeData}
            placeholder="请选择父级目录（不选则为顶级）"
            treeDefaultExpandAll
            allowClear
          />
        </Form.Item>

        <Form.Item
          label="标题"
          name="title"
          rules={[{ required: true, message: '请输入标题' }]}
        >
          <Input placeholder="请输入帮助手册标题" />
        </Form.Item>

        <Form.Item
          label="类型"
          name="type"
          rules={[{ required: true, message: '请选择类型' }]}
        >
          <Select onChange={handleTypeChange}>
            <Option value={0}>目录</Option>
            <Option value={1}>文档</Option>
          </Select>
        </Form.Item>

        {manualType === 1 && (
          <>
            <Form.Item label="封面" name="cover">
              <ImageUpload
                value={coverUrl}
                onChange={handleCoverChange}
                maxCount={1}
                accept="image/*"
                sizeLimit={5}
                placeholder="点击上传封面图片"
              />
            </Form.Item>

            <Form.Item
              label="内容（富文本）"
              name="content"
              rules={[{ required: true, message: '请输入帮助手册内容' }]}
            >
              <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                <Toolbar
                  editor={editor}
                  defaultConfig={toolbarConfig}
                  mode="default"
                  style={{ borderBottom: '1px solid #d9d9d9' }}
                />
                <Editor
                  defaultConfig={editorConfig}
                  value={content}
                  onCreated={setEditor}
                  onChange={handleContentChange}
                  mode="default"
                  style={{ height: '400px', overflowY: 'auto' }}
                />
              </div>
            </Form.Item>
          </>
        )}

        <Form.Item
          label="排序"
          name="sortOrder"
          tooltip="数字越小越靠前"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="状态"
          name="status"
          valuePropName="checked"
        >
          <Switch checkedChildren="启用" unCheckedChildren="禁用" defaultChecked />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/help-manuals')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default HelpManualForm;
